import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Download,
  Upload,
  UtensilsCrossed,
  CheckCircle2,
  XCircle,
  IndianRupee,
  Leaf,
  Drumstick,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  delete_menu_data,
  get_view_category,
  get_view_menu,
  server_post_data,
  show_hide_menu,
  upload_menu_by_csv,
  import_ingredients_from_csv,
} from "../ServiceConnection/serviceconnection";

const foodTypeConfig = {
  veg: { label: "Veg", color: "bg-success/20 text-success", icon: Leaf },
  "non-veg": {
    label: "Non-Veg",
    color: "bg-destructive/20 text-destructive",
    icon: Drumstick,
  },
  egg: { label: "Egg", color: "bg-warning/20 text-warning", icon: Drumstick },
};

export default function MenuItems() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);

  // Dialog states
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [statusChangeMessage, setStatusChangeMessage] = useState("");
  const [loading, setLoading] = useState(true);

  // Filter items
  const filteredItems = useCallback(() => {
    if (!items || items.length === 0) return [];

    return items.filter((item) => {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        item.item_name?.toLowerCase().includes(query) ||
        item.item_code?.toLowerCase().includes(query);

      const matchesCategory =
        categoryFilter === "all" || item.cate_id === categoryFilter;

      const matchesType =
        typeFilter === "all" ||
        item.item_class?.toLowerCase() === typeFilter.toLowerCase();

      return matchesSearch && matchesCategory && matchesType;
    });
  }, [items, searchQuery, categoryFilter, typeFilter])();

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 1);

    try {
      const response = await server_post_data(get_view_category, fd);

      if (!response.data.error) {
        const categoryArray = response.data.message.map((item) => ({
          value: item.primary_id,
          label: item.item_category || "Unnamed Category",
        }));
        setCategories(categoryArray);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setShowLoaderAdmin(false);
    }
  }, []);

  // Fetch items
  const fetchItems = useCallback(
    async (flag = "1", vegNonveg = "", cate = "") => {
      setShowLoaderAdmin(true);
      setLoading(true);
      const fd = new FormData();
      fd.append("flag", flag);
      fd.append("item_class", vegNonveg);
      fd.append("cate_id", cate);

      try {
        const response = await server_post_data(get_view_menu, fd);

        if (!response.data.error) {
          setItems(
            Array.isArray(response.data.message.data)
              ? response.data.message.data
              : [],
          );
        } else {
          setItems([]);
        }
      } catch (error) {
        console.error("Error fetching items:", error);
        setItems([]);
      } finally {
        setShowLoaderAdmin(false);
        setLoading(false);
      }
    },
    [],
  );

  // Handle view item
  const handleViewClick = useCallback((item) => {
    setViewingItem(item);
    setIsViewDialogOpen(true);
  }, []);

  // Handle edit
  const handleEditClick = useCallback(
    (itemId) => {
      navigate("/edit-item", { state: { id: itemId } });
    },
    [navigate],
  );

  // Handle delete
  const handleDeleteClick = useCallback((item) => {
    setSelectedItem(item);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedItem) return;

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag_for", 2);
    fd.append("id_for_delete", selectedItem.primary_id);

    try {
      const response = await server_post_data(delete_menu_data, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete item",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Deleted",
          description: `${selectedItem.item_name} has been deleted successfully`,
        });
        fetchItems();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsDeleteDialogOpen(false);
      setSelectedItem(null);
    }
  }, [selectedItem, toast, fetchItems]);

  // Handle toggle status
  const handleToggleClick = useCallback((item) => {
    setSelectedItem(item);
    setStatusChangeMessage(
      item.active_status
        ? "Are you sure you want to deactivate this item?"
        : "Are you sure you want to activate this item?",
    );
    setIsToggleDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!selectedItem) return;

    setShowLoaderAdmin(true);
    const newStatus = !selectedItem.active_status ? 0 : 1;

    console.log(
      "Toggling status for item:",
      selectedItem.primary_id,
      "to",
      newStatus,
    );

    const fd = new FormData();
    fd.append("item_id", selectedItem.primary_id);
    fd.append("current_status", selectedItem.active_status);
    fd.append("flag_for", "menu");

    try {
      const response = await server_post_data(show_hide_menu, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to update status",
          variant: "destructive",
        });
      } else {
        setItems((prev) =>
          prev.map((item) =>
            item.primary_id === selectedItem.primary_id
              ? { ...item, active_status: !item.active_status }
              : item,
          ),
        );
        toast({
          title: "Success",
          description: `Item ${
            newStatus ? "deactivated" : "activated"
          } successfully`,
        });
      }
    } catch (error) {
      console.error("Toggle error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsToggleDialogOpen(false);
      setSelectedItem(null);
    }
  }, [selectedItem, toast]);

  // Handle bulk upload
  const handleBulkUpload = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv";
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (file) {
        setShowLoaderAdmin(true);
        const fd = new FormData();
        fd.append("file", file);

        try {
          const response = await server_post_data(upload_menu_by_csv, fd);

          if (response.data.error) {
            toast({
              title: "Error",
              description: response.data.message || "Failed to upload file",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Success",
              description: "Bulk data uploaded successfully",
            });
            fetchItems();
          }
        } catch (error) {
          console.error("Upload error:", error);
          toast({
            title: "Error",
            description: "Network error. Please try again.",
            variant: "destructive",
          });
        } finally {
          setShowLoaderAdmin(false);
        }
      }
    };
    input.click();
  }, [toast, fetchItems]);

  // Handle download sample CSV
  const handleDownloadSample = useCallback(() => {
    const sampleData =
      "Name of the menu item/product,Description,Name of the Category,non-veg or veg,Name of the Cuisine,Price: Only in numbers,Final Price after discount: Only in numbers,discount percentage: Only in numbers\n" +
      "Margherita Pizza,Classic cheese pizza Fresh mozzarella Tomato sauce,Pizza,Veg,Italian,500,450,10\n" +
      "Chicken Biryani,Spiced rice with chicken Aromatic and flavorful Served with raita,Pizza,Non-Veg,Indian,350,315,10\n" +
      "Sushi Platter,Assorted sushi rolls Fresh salmon and tuna Served with soy sauce,Pizza,Non-Veg,Japanese,800,720,10";

    const blob = new Blob([sampleData], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_menu_items.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Downloaded",
      description: "Sample CSV file downloaded successfully",
    });
  }, [toast]);

  // Get food type config
  const getFoodTypeConfig = (type) => {
    const typeKey = type?.toLowerCase() || "veg";
    return (
      foodTypeConfig[typeKey] || {
        label: type,
        color: "bg-muted text-muted-foreground",
        icon: UtensilsCrossed,
      }
    );
  };

  // Initial load
  useEffect(() => {
    fetchCategories();
    fetchItems();
  }, [fetchCategories, fetchItems]);

  return (
    <MainLayout>
      <Header
        title="Menu Items Management"
        subtitle="Manage your restaurant menu items and dishes"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <UtensilsCrossed className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {items.length}
                </p>
                <p className="text-sm text-muted-foreground">Total Items</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {items.filter((i) => i.active_status).length}
                </p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {
                    items.filter((i) => i.item_class?.toLowerCase() === "veg")
                      .length
                  }
                </p>
                <p className="text-sm text-muted-foreground">Veg Items</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <Drumstick className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {
                    items.filter(
                      (i) => i.item_class?.toLowerCase() === "non-veg",
                    ).length
                  }
                </p>
                <p className="text-sm text-muted-foreground">Non-Veg Items</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="flex flex-1 gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Food Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="veg">Veg</SelectItem>
                  <SelectItem value="non-veg">Non-Veg</SelectItem>
                  <SelectItem value="egg">Egg</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleDownloadSample}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Sample CSV
              </Button>
              <Button
                variant="outline"
                onClick={handleBulkUpload}
                className="gap-2"
              >
                <Upload className="w-4 h-4" />
                Bulk Upload
              </Button>
              <Button
                onClick={() => navigate("/add-item")}
                className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </Button>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Sr.No</TableHead>
                <TableHead className="text-muted-foreground">
                  Item Name
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Item Code
                </TableHead>
                <TableHead className="text-muted-foreground">Price</TableHead>
                <TableHead className="text-muted-foreground">Type</TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-12 text-muted-foreground"
                  >
                    Loading items...
                  </TableCell>
                </TableRow>
              ) : filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <UtensilsCrossed className="w-8 h-8" />
                      <p>No items found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((item, index) => {
                  const typeConfig = getFoodTypeConfig(item.item_class);
                  const TypeIcon = typeConfig.icon;

                  return (
                    <TableRow
                      key={item.primary_id}
                      className="border-border hover:bg-muted/30"
                    >
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                            <UtensilsCrossed className="h-5 w-5 text-primary-foreground" />
                          </div>
                          <p className="font-medium text-foreground">
                            {item.item_name}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {item.item_code}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 font-medium text-foreground">
                          <IndianRupee className="w-3 h-3" />
                          {item.item_original_price}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={cn(
                            "font-medium border-0 gap-1",
                            typeConfig.color,
                          )}
                        >
                          <TypeIcon className="w-3 h-3" />
                          {typeConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleToggleClick(item)}
                          className={cn(
                            "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                            item.active_status
                              ? "bg-success"
                              : "bg-muted-foreground/30",
                          )}
                        >
                          <span
                            className={cn(
                              "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                              item.active_status
                                ? "translate-x-6"
                                : "translate-x-1",
                            )}
                          />
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewClick(item)}
                            className="hover:bg-primary/10 hover:text-primary"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(item.primary_id)}
                            className="hover:bg-primary/10 hover:text-primary"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(item)}
                            className="hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* View Item Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Item Description</DialogTitle>
              <DialogDescription>
                Complete description of this menu item
              </DialogDescription>
            </DialogHeader>

            {viewingItem && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                    <UtensilsCrossed className="h-8 w-8 text-primary-foreground" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-foreground">
                      {viewingItem.item_name}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Code: {viewingItem.item_code}
                    </p>
                  </div>
                  <Badge
                    className={cn(
                      "font-medium border-0 gap-1",
                      getFoodTypeConfig(viewingItem.item_class).color,
                    )}
                  >
                    {(() => {
                      const TypeIcon = getFoodTypeConfig(
                        viewingItem.item_class,
                      ).icon;
                      return <TypeIcon className="w-3 h-3" />;
                    })()}
                    {getFoodTypeConfig(viewingItem.item_class).label}
                  </Badge>
                </div>

                <div className="p-4 border rounded-lg bg-card">
                  <h4 className="font-semibold text-foreground mb-2">
                    Description
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {viewingItem.item_description || "No description available"}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg bg-card">
                    <p className="text-xs text-muted-foreground mb-1">
                      Original Price
                    </p>
                    <div className="flex items-center gap-1 text-lg font-semibold text-foreground">
                      <IndianRupee className="w-4 h-4" />
                      {viewingItem.item_original_price}
                    </div>
                  </div>
                  <div className="p-4 border rounded-lg bg-card">
                    <p className="text-xs text-muted-foreground mb-1">Status</p>
                    <Badge
                      className={cn(
                        "font-medium border gap-1",
                        viewingItem.active_status
                          ? "bg-success/20 text-success"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      {viewingItem.active_status ? (
                        <>
                          <CheckCircle2 className="w-3 h-3" />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircle className="w-3 h-3" />
                          Inactive
                        </>
                      )}
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsViewDialogOpen(false)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setIsViewDialogOpen(false);
                  handleEditClick(viewingItem.primary_id);
                }}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Item
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Item</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "
                <strong>{selectedItem?.item_name}</strong>"? This action cannot
                be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={showLoaderAdmin}
                className="bg-destructive hover:bg-destructive/90"
              >
                {showLoaderAdmin ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Toggle Status Confirmation Dialog */}
        <AlertDialog
          open={isToggleDialogOpen}
          onOpenChange={setIsToggleDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Item Status</AlertDialogTitle>
              <AlertDialogDescription>
                {statusChangeMessage}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmToggle}
                disabled={showLoaderAdmin}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin ? "Updating..." : "Confirm"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
